<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, onMounted, getCurrentInstance, watch, useTemplateRef, nextTick } from 'vue'
import _ from 'lodash'
import moment from 'moment';
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification";
import UserService from "@/services/UserService";
import Spinner from '@/components/ui/Spinner.vue'
import IconBase from '@/components/icon/IconBase.vue';
import StreamService from '@/services/StreamService';
import Utils, { sortStreams } from '@/utils';
import { usePersistedSort, DATE_NAME_SORT } from '@/Utils/usePersistedSort';
import { getDashboardUrl } from '@/Utils/authRedirect';
import StreamCardView from '@/components/ui/StreamCardView.vue';
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import ViewModeSwitcher from '@/components/ui/ViewModeSwitcher.vue';
import IconCameraPlus from '../components/icon/IconCameraPlus.vue';
import IconCamera from '../components/icon/IconCamera.vue';
import IconPlus from '@/components/icon/IconPlus.vue';
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue'
import FormButton from '@/components/Atoms/FormButton.vue';
import PageHeader from '@/components/ui/PageHeader.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue';
import SidebarAddStream from '../components/modals/SidebarAddStream.vue';
import Alert from '../components/Atoms/Alert.vue';
import DropDownMenu from '../components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../components/Atoms/DropDownMenuItem.vue';
import Badge from '../components/Badge.vue';
import Tooltip from '../components/Atoms/Tooltip.vue';
import { planLimitReachedMessage } from '@/Utils/planLimitCopy'
import { hasProductAccess } from '@/Utils/teamPermissionRows'
import CollapseContainer from '@/components/Atoms/CollapseContainer.vue';
import Pagination from '../components/ui/Pagination.vue';
import { track, EVENTS } from '@/lib/tracking';
import ListingCreateTile from '@/components/ui/ListingCreateTile.vue';
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue';
import ListingSearchEmpty from '@/components/ui/ListingSearchEmpty.vue';
// PhoneNumberRequiredModal removed - auto-show was disabled, only needed on specific pages

const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()
const store = useStore()
const { notify } = useNotification()

const MAX_PAGE_VIEW = 30

// Emits
const emit = defineEmits({
	'updateTitle': () => true
})

//TemplateRefs
const sidebarAddStream = useTemplateRef('sidebarAddStream')
const sortDropdown = useTemplateRef('sortDropdown')

// Refs
const collapseRefs = ref({})
const downgradeDatedifference = ref(0)
const withinLast3Days = ref(false)
const searchWidgetInput = ref('')
const deleteModalConfiguredStream = ref(null)
const deleteProcessing = ref(false)
const deleteIndex = ref(null)
const categoryNameMaps = ref({
	live: 'Streams',
	restream: 'Multistream',
	ipcam: 'IP Camera',
})
const categoryCollapseHash = ref({
	restream: true,
	live: true,
	ipcam: true,
})
const { sortBy, setSort } = usePersistedSort('castr.sort.live', DATE_NAME_SORT, 'Newest')
const action = ref(null)
const team_id = ref(null)
const teamownerName = ref(null)
const showcase = ref(null)
const showcasedStream = ref(null)
const streamCategoryList = ref(['restream', 'live'])
const currentPage = ref(0)

// Computed
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const shouldShowCompactModeStreams = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])
const baseSubscription = computed(() => store.getters['User/baseSubscription'])
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])
const subscriptions = computed(() => store.getters['User/subscriptions'])
const activeTeam = computed(() => store.getters['User/activeTeam'])
const multiStreams = computed(() => store.getters['Streams/multiStreams'])
const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])
const streams = computed(() => store.getters['Streams/streams'])
const userPermissions = computed(() => store.getters['User/userPermissions'])
const userEmail = computed(() => store.getters['User/userEmail'])
const memberPermission = computed(() => activeTeam.value && userPermissions.value)
const canSeeLiveStreams = computed(() => !memberPermission.value || hasProductAccess(memberPermission.value, 'live'))
const canSeeRestreams = computed(() => !memberPermission.value || hasProductAccess(memberPermission.value, 'restream'))
// This page lists both livestreams and legacy multistreams. Either product
// flag is enough to enter; each list is still filtered by its own flags.
const hasAccessToLivePage = computed(() => canSeeLiveStreams.value || canSeeRestreams.value)

const allInOneStreamsFiltered = computed(() => {
	if (!canSeeLiveStreams.value) return []
	const searchStr = searchWidgetInput.value.toLowerCase();
	const result = allInOneStreams.value.filter(stream => stream.name.toLowerCase().includes(searchStr))
	return sortStreams(result, sortBy.value) || result
})

const allInOneStreamsEnabled = computed(() => {
	const result = allInOneStreams.value.filter(stream => stream.enabled)
	return result
})

const allInOneStreamsSliced = computed(() => {
	const startIndex = currentPage.value * MAX_PAGE_VIEW
	return allInOneStreamsFiltered.value.slice(startIndex, startIndex + MAX_PAGE_VIEW)
})

const multiStreamsFiltered = computed(() => {
	if (!canSeeRestreams.value) return []
	const searchStr = searchWidgetInput.value.toLowerCase();
	const result = multiStreams.value.filter(stream => stream.name.toLowerCase().includes(searchStr))
	return sortStreams(result, sortBy.value) || result
})

// Access-aware stream lists that ignore the search filter. The grouping frame
// and the list controls must stay stable while searching: keying them off the
// *filtered* counts made the search box, sort control and section headers vanish
// the moment a query narrowed the list (e.g. to a single match or to one
// category), which is a regression. These still honour product-level access
// (a member without live access sees no live streams), just not the query.
const allInOneStreamsAccessible = computed(() => (canSeeLiveStreams.value ? allInOneStreams.value : []))
const multiStreamsAccessible = computed(() => (canSeeRestreams.value ? multiStreams.value : []))

// Only group (and offer collapse) when the user actually has both stream types.
const isGrouped = computed(() => multiStreamsAccessible.value.length > 0 && allInOneStreamsAccessible.value.length > 0)

// Sort + search only add value once there's a meaningful list to manage.
const showListControls = computed(() => (multiStreamsAccessible.value.length + allInOneStreamsAccessible.value.length) > 2)
const sortByLabel = computed(() => sortBy.value)
// When not grouped, exactly one category has streams; its label/count shares the controls row.
const singleCategory = computed(() => {
	if (allInOneStreamsAccessible.value.length > 0) return 'live'
	if (multiStreamsAccessible.value.length > 0) return 'restream'
	return null
})

const allInOneStreamsFilteredNumber = computed(() => allInOneStreamsFiltered.value.length)

const shouldShowPagination = computed(() => allInOneStreamsFilteredNumber.value > MAX_PAGE_VIEW)

const pages = computed(() => {
	return Math.ceil(allInOneStreamsFilteredNumber.value / MAX_PAGE_VIEW)
})

const shouldShowPlaceholder = computed(() => {
	if (streamCategoryList.value?.length === 0) return true
	// Only the genuine "no streams" state shows the onboarding placeholder — a
	// search that matches nothing must leave the (empty) list and its controls in
	// place so the query can be cleared, not swap in the create-your-first-stream
	// screen. So key this off the access-aware counts, not the filtered ones.
	if (multiStreamsAccessible.value?.length === 0 && allInOneStreamsAccessible.value?.length === 0) return true
	return false
})

// A query that misses both lists. The controls row above stays put (it is
// how the user recovers), so the empty grid below needs to say why it is empty
// rather than leave the page blank.
const noSearchMatches = computed(() => (
	!!searchWidgetInput.value
	&& allInOneStreamsFiltered.value.length === 0
	&& multiStreamsFiltered.value.length === 0
))

const clearSearch = () => {
	searchWidgetInput.value = ''
}

const activeLiveSub = computed(() => {
	return addonSubscriptions.value.find(subscription => subscription.category === 'live' && subscription.enabled)
})

/**
 * Offer a pending invitation, if the link that got us here carries one.
 *
 * Deliberately not awaited by the hook below, and holding its own errors: the
 * prompt used to sit after the dashboard's own reads, so a failed one rejected
 * out of `onMounted` before the invitation was ever offered — and the invitee
 * had no other way to accept. Nothing here depends on streams or the showcase.
 */
async function promptToJoinTeam(teamID) {
	team_id.value = teamID;
	try {
		const owner = await UserService.getTeamOwner(teamID, true);
		teamownerName.value = owner.teamName;
		// Every invitee confirms explicitly — a fresh sign-up included. Signing up
		// is not consent to join a particular team. `from=signup` still rides the
		// query for the TEAM_JOINED entry_source, read in `joinTeam` on confirm.
		// nextTick ensures the join-team modal is registered before we open it.
		await nextTick();
		showModal('join-team');
	} catch (err) {
		notify({
			group: "error",
			text: err.message || "Something went wrong. Please try again later.",
		});
	}
}

// Hooks
onMounted(async () => {
	store.dispatch('Ui/setLoadingStatus', true)

	const teamID = route.query?.teamID;
	if (teamID) promptToJoinTeam(teamID);

	await loadStreams()
	const streamsCount = _.size(streams.value)
	// The showcase is one card on this page, not the page. An account that
	// cannot read it — a member still waiting on a grant gets a 403 — used to
	// reject out of this hook and take everything after it down with it.
	showcase.value = await UserService.getShowcase().catch(() => null)

	// event tracking
	window.trackEvent('Page Viewed', {
		page: 'Dashboard',
		totalStreams: streamsCount,
		enabledStreams: _.size(_.filter(streams.value, { enabled: true }))
	})

	if (window.Intercom) {
		window.Intercom('update', {
			totalStreams: streamsCount,
			totalLiveStreams: allInOneStreams.value.length
		});
	}

	// update window title
	emit('updateTitle', 'Dashboard - Castr Streams');

	onViewLoaded();

	action.value = route.query?.action;

	if (action.value === 'restream' || action.value === 'live') {
		showModal('modal-add-stream');
	}
})

// Watchers
watch(activeTeam, () => {
	loadStreams();
})
watch(searchWidgetInput, () => {
	currentPage.value = 0
})
// Methods
const notAllowed = (categoryName, currentSreams) => {
	const currentSreamsNumber = currentSreams.filter(stream => stream.enabled).length
	if (categoryName === 'live') {
		const subscription = addonSubscriptions.value.find(subscription => subscription.category === 'live' && subscription.enabled)
		const maxStreamsAllowed = _.get(subscription, 'definitionOverride.streams', _.get(subscription, 'package.definition.streams', false))
		return currentSreamsNumber >= parseInt(maxStreamsAllowed) ? true : false
	}
	if (categoryName === 'restream') {
		const maxStreamsAllowed = _.get(baseSubscription.value, 'definitionOverride.streams', _.get(baseSubscription.value, 'package.definition.streams', false))
		return currentSreamsNumber >= parseInt(maxStreamsAllowed) ? true : false
	}
	return true
}

const streamFilter = (categoryName) => {
	if (categoryName === 'live') return _.cloneDeep(allInOneStreamsSliced.value)
	if (categoryName === 'restream') return _.cloneDeep(multiStreamsFiltered.value)
	return
}

const streamsCreated = (categoryName) => {
	if (categoryName === 'live') return _.cloneDeep(allInOneStreams.value).length
	if (categoryName === 'restream') return _.cloneDeep(multiStreams.value).length
	return
}

const setRef = (el, key) => {
	if (el) {
		collapseRefs.value[key] = el;
	}
}

const toggleCollapse = (categoryName) => {
	categoryCollapseHash.value[categoryName] = !categoryCollapseHash.value[categoryName]
	collapseRefs.value[categoryName + 'streams'].toggleView()
}

const getDowngradedate = () => {
	if (baseSubscription.value || addonSubscriptions.value) {
		let date = [];

		const lastActivateddate = Math.max.apply(null, date);
		if (lastActivateddate) {
			// const millisecondsPerDay = 5 * 60 * 1000;
			const millisecondsPerDay = 24 * 60 * 60 * 1000;

			const currentDate = new Date();
			const givenDate = new Date(lastActivateddate);
			const timeDifference = currentDate.getTime() - givenDate.getTime();

			const daysDifference = Math.floor(timeDifference / millisecondsPerDay);
			withinLast3Days.value = (daysDifference < 3);
			downgradeDatedifference.value = daysDifference;
		}
	}
}

const loadStreams = async (forceRefresh = false) => {
	try {
		await store.dispatch('Streams/getStreams', forceRefresh)
		store.dispatch('Ui/setLoadingStatus', false);
		getDowngradedate();

	} catch (err) {
		store.dispatch('Ui/setLoadingStatus', false);
		return;
	}
}

const streamsCount = (type) => {
	if (type === 'restream') return '/' + (baseSubscription.value?.definitionOverride?.streams ?? baseSubscription.value?.package?.definition?.streams ?? 0)
	if (type === 'live') return '/' + (addonSubscriptions.value?.find(subscription => subscription.category === 'live')?.definitionOverride?.streams ?? addonSubscriptions.value?.find(subscription => subscription.category === 'live')?.package?.definition?.streams ?? 0)
	return '/' + (0)
}

const getAllocatedStreamNumber = (type, number = false) => {
	if (type === 'restream') {
		const maxMultistreamNumber = _.get(baseSubscription.value, 'definitionOverride.streams', _.get(baseSubscription.value, 'package.definition.streams', 0));
		return maxMultistreamNumber
	}
	if (type === 'live') {
		const allInOnePackage = addonSubscriptions.value.find(subscribtion => (subscribtion.package.category === 'live') && subscribtion.enabled);
		const maxAllInOneNumber = _.get(allInOnePackage, 'definitionOverride.streams', _.get(allInOnePackage, 'package.definition.streams', 0));
		return maxAllInOneNumber
	}
	return
}

const liveStreamLimit = computed(() => {
	const c = getAllocatedStreamNumber('live')
	return typeof c === 'number' && c > 0 ? c : null
})

const restreamLimit = computed(() => {
	const c = getAllocatedStreamNumber('restream')
	return typeof c === 'number' && c > 0 ? c : null
})

// `used` is what this workspace holds; the meter pairs it with the account's
// total, since an empty workspace can still sit on a plan that is fully spent.
const liveHeaderUsage = computed(() => {
	if (loadingStatus.value) return null
	const meta = {
		used: allInOneStreams.value?.length || 0,
		label: 'streams',
		labelSingular: 'stream',
		resource: 'live',
	}
	if (liveStreamLimit.value) meta.limit = liveStreamLimit.value
	return meta
})

const atLivestreamLimit = computed(() => {
	const max = liveStreamLimit.value
	if (!max) return false
	return (allInOneStreams.value?.length ?? 0) >= max
})

const atMultistreamLimit = computed(() => {
	const max = restreamLimit.value
	if (!max) return false
	return (multiStreams.value?.length ?? 0) >= max
})

const hasMultistreamSubscription = computed(() => store.getters['User/hasMultistreamSubscription'])
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription'])

const hasBothStreamTypes = computed(() =>
	!!(hasMultistreamSubscription.value && hasLiveSubscription.value?.enabled)
)

// Header create stays available if either stream type still has quota.
const atAllCreateLimits = computed(() => {
	if (hasBothStreamTypes.value) return atLivestreamLimit.value && atMultistreamLimit.value
	if (hasLiveSubscription.value?.enabled) return atLivestreamLimit.value
	if (hasMultistreamSubscription.value) return atMultistreamLimit.value
	return false
})

const isLivestreamCreateDisabled = computed(() =>
	atAllCreateLimits.value || (memberPermission.value && !memberPermission.value.liveStreamManage && !memberPermission.value.recordedStreamManage)
)

const livestreamCreateDisabledTitle = computed(() => {
	if (!atAllCreateLimits.value) return ''
	if (hasBothStreamTypes.value) return planLimitReachedMessage('Stream')
	if (atLivestreamLimit.value) return planLimitReachedMessage('Livestream')
	return planLimitReachedMessage('Multistream')
})

const changeSortBy = (val) => {
	setSort(val);
	sortDropdown.value?.toggleDropdown()
}

const toggleCompactMode = (value) => {
	store.dispatch('Ui/setCompactModeStreams', value);
}

const getCategoryIcon = (categoryName) => {
	if (categoryName === 'subsecond') {
		return
	}
	return require(`@/assets/icons/stream-type-${categoryName}-ico.svg`); // eslint-disable-line no-undef
}

const updateStreamPulse = (streamId, pulseData) => {
	const sindex = _.findIndex(streams.value, s => s._id === streamId)
	if (sindex > -1) {
		const updatedStream = { ...streams.value[sindex], pulse: pulseData }
		const updatedStreams = Utils.updateArrayItem(
			_.cloneDeep(streams.value),
			updatedStream,
			sindex
		)
		store.dispatch('Streams/updateStreams', updatedStreams)
	}
}

const checkMultistream = (stream) => {
	if (baseSubscription.value.enabled && baseSubscription.value?.package?.category === 'restream') {
		showModal('modalToggleStream' + stream._id, '#btnShow')
	} else {
		showModal('create-aio')
	}
}

const onViewLoaded = () => {
	const qs = Utils.parseQueryString(window.location.search) || {}
	if (qs.action === 'newstream') {
		let streamCreationType = qs.streamtype || ''
		streamCreationType =
			streamCreationType === 'restream' ? '' : streamCreationType
		if (streamCreationType) streamCreationType += '-'

		const streamCreationModalId = `modal-add-${streamCreationType}channel`

		showModal(streamCreationModalId)
	}
}

const onNewStream = async (stream, regionDetails) => {
	notify({ group: 'success', text: 'Stream deployed successfully' })

	// Redirect immediately for seamless UX
	let redirectPath = '/multistreams/'
	if (stream.type === 'live') {
		redirectPath = '/livestreams/'
	}
	redirectPath += stream._id
	router.push({ path: redirectPath })

	// Refresh store in background
	store.dispatch('Streams/getStreams', true)
	
	// track event
	window.trackEvent('Stream Deployed', {
		streamId: stream._id,
		streamName: stream.name,
		streamType: stream.type,
		region: regionDetails ? regionDetails.name : undefined,
	});
}

const onStreamDeleteRequest = (stream) => {
	// Check if this stream is being used as a linked source destination
	const allStreams = streams.value || []
	const streamsUsingThisAsDestination = allStreams.filter(otherStream => {
		if (!otherStream.platforms || otherStream._id === stream._id) return false
		return otherStream.platforms.some(platform => 
			platform.enabled && 
			platform.linkedSource && 
			platform.streamObject && 
			(platform.streamObject._id === stream._id || platform.streamObject === stream._id)
		)
	})
	
	if (streamsUsingThisAsDestination.length > 0) {
		const streamNames = streamsUsingThisAsDestination.map(s => s.name).join(', ')
		notify({
			group: 'error',
			title: 'Cannot delete stream',
			text: `This livestream is currently used as a linked source destination in ${streamNames} livestream(s). Remove it from those livestreams to proceed with deletion.`
		})
		return
	}
	
	deleteModalConfiguredStream.value = stream;
	deleteIndex.value = streams.value.indexOf(stream);
	let modalId = "delete-stream"

	if (showcase.value && showcase.value.streams.length > 0) {
		const hasShowcased = showcase.value.streams.filter((sw) => {
			return sw._id == stream._id
		});
		if (hasShowcased.length > 0) {
			showcasedStream.value = hasShowcased[0]
			modalId = "delete-stream-showcased"
		}
	}
	showModal(modalId)
}

const onStreamDeleteConfirmShowcased = async () => {
	const filteredShowcase = [];
	showcase.value.streams.map(sw => {
		if (sw._id != showcasedStream.value._id) {
			filteredShowcase.push(sw._id)
		}
	})
	const filteredVod = [];
	showcase.value.vods.map(sw => {
		let vid = (sw.refId ? sw.refId : sw.id)
		filteredVod.push(vid)
	});
	const streamOrder = showcase.value.streamOrder.filter(sw => {
		return sw != showcasedStream.value._id
	});
	showcase.value.streams = filteredShowcase
	let updateShowcase = await UserService.addShowcase(filteredShowcase, filteredVod, streamOrder, showcase.value._id);
	onStreamDeleteConfirm()
}

const gotoUpgrade = () => {
	window.location.href = `/app/subscribe?category=live`;
}

const openCreatestream = () => {
	sidebarAddStream.value.toggleSidebar()
}
const onStreamDeleteConfirm = async () => {
	// pop stream out of the list
	deleteProcessing.value = true;

	// Try to delete its dvr
	let vodEpisodes
	let permanentDvrArchives
	try {
		vodEpisodes = await StreamService.getStreamDvrArchives(deleteModalConfiguredStream.value._id);
	} catch (e) {
		notify({
			group: 'error',
			text: 'could not retrieve saved vod(s)'
		});
	}

	vodEpisodes = _.map(vodEpisodes, vodEpisode => {
		const ep = _.assign({}, vodEpisode, {
			startTimeDate: new Date(vodEpisode.startTime),
			endTimeDate: new Date(vodEpisode.endTime)
		});

		ep.fileName = `${moment(new Date(String(ep.startTimeDate))).fromNow()}`
		ep.fileName = ep.fileName.replace('a few seconds ago', 'a seconds ago');
		ep.creationTime = ep.startTimeDate;

		ep.live = false;
		ep.expanded = false;
		ep.streamType = deleteModalConfiguredStream.value.type;
		ep.blocking = false;
		// ep.size = ep.size ? this.$filters.bytes(ep.size, false, 2) : '--';

		ep.exportProcessing = false;
		ep.exportStatusTimer = null;

		return ep;
	});

	permanentDvrArchives = _.filter(vodEpisodes, { exportStatus: 'EXPORTED' }) || []
	permanentDvrArchives.forEach(async (video) => {
		const q = {
			streamId: deleteModalConfiguredStream.value._id,
			from: video.from,
			duration: video.duration,
			abr: video.abr
		};
		if (!video.pseudoVod) {
			await StreamService.deleteStreamDvrArchive(q);
		}
	});

	try {
		await StreamService.deleteStream(deleteModalConfiguredStream.value._id);
		// Force refresh to remove deleted stream from list
		await store.dispatch('Streams/getStreams', true)
		await store.dispatch('PlatformUsage/getPlatformUsage', {
			type: deleteModalConfiguredStream.value.type,
			force: true,
		})
		// track event
		const removedStream = deleteModalConfiguredStream.value;
		window.trackEvent('Stream Deleted', {
			streamId: removedStream._id,
			streamName: removedStream.name,
			streamType: removedStream.type,
		});
	} catch (err) {
		notify({ group: 'error', title: err.error, text: err.message });
		// push stream back to list
		onNewStream(deleteModalConfiguredStream.value);
	}

	deleteModalConfiguredStream.value = null;
	deleteIndex.value = null;
	deleteProcessing.value = false;
	notify({
		group: 'success',
		text: 'Deleted successfully'
	});
}

const onStreamToggleConfirm = async (ev, newStatus, stream = null) => {
	try {
		await StreamService.toggleStream(stream._id);
		// Force refresh to get updated stream state
		await loadStreams(true);
	} catch (err) {
		notify({
			group: 'error',
			title: "Couldn't toggle stream status",
			text: err.message
		});

		if (err.message && err.message.indexOf('upgrade') > -1) {
			showModal('billing-prompt');
		}
	}
}

const joinTeam = async () => {
	store.dispatch('Ui/setLoadingStatus', true);
	try {
		const res = await UserService.joinTeam(team_id.value);
		if (res.success) {
			const select = await UserService.selectTeam(team_id.value);
			const loadActiveteam = await UserService.getMyTeam();
			track(EVENTS.TEAM_JOINED, {
				entry_source: route.query?.from === 'signup' ? 'signup' : 'invite_link',
			});
			notify({
				group: "success",
				text: `You're successfully logged in into ${loadActiveteam.name} team`,
			});
			setTimeout(() => {
				// Not '/': the app is served under /app/, so the site root is the
				// marketing page. The join itself had worked — the token from
				// `selectTeam` was already stored — so the invitee landed signed
				// out-looking on castr.com and had to find their way back in.
				window.location.replace(getDashboardUrl());
			}, 1000);
		}
	} catch (err) {
		// this.loading = false;
		store.dispatch('Ui/setLoadingStatus', false);
		notify({ group: 'error', title: err.error, text: err.message });
		return;
	}
}

const showCreateStreamSidebar = () => {
	sidebarAddStream.value.toggleSidebar()
}

const showCreateStreamSidebarFor = (type) => {
	sidebarAddStream.value?.openForType(type)
}

const canCreateStreamType = (type) => {
	if (memberPermission.value && !memberPermission.value.liveStreamManage && !memberPermission.value.recordedStreamManage) return false
	if (type === 'live') return canSeeLiveStreams.value
	if (type === 'restream') return canSeeRestreams.value
	return false
}

const streamTypeAtLimit = (type) => type === 'live' ? atLivestreamLimit.value : atMultistreamLimit.value
const streamTypeLimit = (type) => type === 'live' ? liveStreamLimit.value : restreamLimit.value
const streamTypeUsed = (type) => type === 'live' ? allInOneStreams.value.length : multiStreams.value.length
const streamTypeLabel = (type) => type === 'live' ? 'Livestream' : 'Multistream'

const hasEnabledLinkedSources = (stream) => {
	return stream?.platforms?.some(platform => platform.enabled && platform.linkedSource) || false;
}

const getTooltipLinkedSourceText = (stream) => {
	const enabledLinkedSource = stream?.platforms?.find(platform => platform.enabled && platform.linkedSource);
	if (!enabledLinkedSource || !enabledLinkedSource?.streamObject) return ''
	const linkedStreamName = enabledLinkedSource.streamObject.name
	return `This livestream cannot be deleted because it is being used as a source in ${linkedStreamName} livestream. To delete this livestream, first remove it as a source from ${linkedStreamName} livestream.`
}

const goToPage = (page) => currentPage.value = page

const shouldShowDivider = (index) => {
	if ((index !== 0) && (multiStreams.value.length > 0) && (allInOneStreams.value.length > 0)) return true
	return false
}
const categoryNameTitle = (categoryName) => {
	return categoryNameMaps.value[categoryName] || categoryName
}
</script>

<template>
	<div class="container detail-shell page-title-area px-4">
		<PageHeader
			title="Livestreams"
			subtitle="Host and manage your live streams end to end."
			mobile-inline
			:usage="liveHeaderUsage"
		>
			<Tooltip :active="atAllCreateLimits" wrap-trigger :html="true" :hide="1000" :content="livestreamCreateDisabledTitle">
				<FormButton v-if="!loadingStatus" class="gap-1" size="md" @click="showCreateStreamSidebar"
					:disabled="isLivestreamCreateDisabled">
					<icon-base class="mr-2" fill="none">
						<icon-plus />
					</icon-base>
					New <span class="hidden md:inline">Livestream</span>
				</FormButton>
			</Tooltip>
		</PageHeader>
		<div v-if="loadingStatus" class="flex items-center justify-center w-full min-h-[70vh]" aria-busy="true">
			<Spinner text="Loading your streams…" classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
		</div>
		<div v-else-if="!activeTeam || hasAccessToLivePage">
			<section class="service-tab">
				<Alert v-if="activeLiveSub && allInOneStreamsEnabled.length > getAllocatedStreamNumber('live')" type="normal">
					<div class="flex flex-col">
						<span class="text-dark-9 text-base">Your plan allows {{ getAllocatedStreamNumber('live') }} streams, but you have {{ allInOneStreams.length }} enabled now.</span>
						<span class="text-dark-7 text-sm">Please disable/delete {{ Math.abs(getAllocatedStreamNumber('live') - allInOneStreams.length) }} streams within 2 days, or we'll disable them automatically. We are not liable for non-compliance.</span>
					</div>
				</Alert>
				<!-- Consolidated controls row: single-group count label on the left, compact utilities on the right. -->
				<ResponsiveToolbar v-if="!shouldShowPlaceholder" class="mt-6 mb-3">
					<!-- Only the single (non-grouped) view shows its count here; grouped view keeps per-section labels below. -->
					<template #leading v-if="!isGrouped && singleCategory">
						<div class="flex items-center gap-2">
							<icon-base v-if="singleCategory === 'live'" class="size-4 shrink-0 text-main-light" fill="none" width="16" height="16">
								<icon-camera />
							</icon-base>
							<img v-else :src="getCategoryIcon(singleCategory)" :alt="singleCategory" class="size-4 shrink-0">
							<span class="text-base font-medium text-surface-9">{{ categoryNameTitle(singleCategory) }}</span>
						</div>
					</template>
					<template v-if="showListControls">
						<DropDownMenu ref="sortDropdown" type="secondary" position="left">
							<template #toggle-button>
								<span class="text-surface-8">Sort by:&nbsp;</span>
								<span class="text-white">{{ sortByLabel }}</span>
								<icon-base class="!ml-1 shrink-0 text-surface-8">
									<icon-chevron-up-down />
								</icon-base>
							</template>
							<template #menu-items>
								<DropDownMenuItem :active="sortBy === 'Oldest'" @click="changeSortBy('Oldest')">
									Oldest
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Oldest'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'Newest'" @click="changeSortBy('Newest')">
									Newest
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Newest'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'A to Z'" @click="changeSortBy('A to Z')">
									A to Z
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'A to Z'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'Z to A'" @click="changeSortBy('Z to A')">
									Z to A
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Z to A'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
							</template>
						</DropDownMenu>
						<ListingSearchInput v-model="searchWidgetInput" />
					</template>
					<ViewModeSwitcher class="shrink-0" />
				</ResponsiveToolbar>
				<div :class="{ 'list-view': shouldShowCompactModeStreams }" class="castr-tabs">
					<div v-for="(categoryName, index) in streamCategoryList" :key="categoryName">
						<!-- Grouped view keeps its collapsible per-section headers; the single view shows its label in the controls row above. -->
						<component :is="isGrouped ? 'button' : 'div'" v-if="isGrouped && streamFilter(categoryName)?.length > 0"
							:type="isGrouped ? 'button' : null" :aria-controls="categoryName + '-streams'"
							:aria-expanded="isGrouped ? (categoryCollapseHash[categoryName] ? 'true' : 'false') : null"
							class="flex items-center gap-2 w-full text-left mb-3"
							:class="[index !== 0 && isGrouped ? 'mt-8' : '', isGrouped ? 'group cursor-pointer' : 'cursor-default']"
							@click="isGrouped && toggleCollapse(categoryName)">
							<icon-base v-if="isGrouped" fill="none"
								class="shrink-0 text-surface-6 group-hover:text-surface-8 transition-transform duration-200"
								:class="categoryCollapseHash[categoryName] ? 'rotate-0' : '-rotate-90'">
								<icon-chevron-down />
							</icon-base>
							<icon-base v-if="categoryName === 'live'" class="size-4 shrink-0 text-main-light" fill="none" width="16" height="16">
								<icon-camera />
							</icon-base>
							<img v-else :src="getCategoryIcon(categoryName)" :alt="categoryName" class="size-4 shrink-0">
							<span class="text-base font-medium text-surface-9">{{ categoryNameTitle(categoryName) }}</span>
							<Tooltip :active="!!streamsCount(categoryName)" :hide="600">
								<Badge variant="dark-3" :id="'stream_count_' + categoryName" class="stream-count" pill>{{
									streamsCreated(categoryName)
								}}{{ streamsCount(categoryName) }}
								</Badge>
								<template #content>
									<span class="text-dark-8 text-small">
										You have created {{ streamsCreated(categoryName) }}{{ streamsCount(categoryName) }} streams.
										Need more? <a class="underline text-white" target="_blank" href="/app/subscribe">Upgrade here</a>
									</span>
								</template>
							</Tooltip>
						</component>
						<CollapseContainer class="mt-3" :ref="el => setRef(el, categoryName + 'streams')" :is-open="true">
							<div class="grid grid-cols-1 auto-rows-fr px-px"
								:class="shouldShowCompactModeStreams ? '!grid-cols-1 gap-1.5' : 'md:grid-cols-2 lg:grid-cols-3 gap-4'">
								<stream-card-view v-for="stream in streamFilter(categoryName)" :key="stream._id" :stream="stream"
									:notAllowed="notAllowed(categoryName, streamFilter(categoryName))"
									:compact-mode="shouldShowCompactModeStreams" :deleteBlock="deleteProcessing"
									@delete-stream="onStreamDeleteRequest" @pulse-update="updateStreamPulse"
									@check-multistream="checkMultistream" :hasEnabledLinkedSources="hasEnabledLinkedSources(stream)"
									:tooltipLinkedSourceText="getTooltipLinkedSourceText(stream)" />
								<ListingCreateTile
									v-if="streamFilter(categoryName)?.length && !searchWidgetInput && canCreateStreamType(categoryName)"
									:label="streamTypeLabel(categoryName)"
									:view="shouldShowCompactModeStreams ? 'list' : 'thumbnail'"
									:used="streamTypeUsed(categoryName)"
									:limit="streamTypeLimit(categoryName)"
									:limit-reached="streamTypeAtLimit(categoryName)"
									@create="showCreateStreamSidebarFor(categoryName)"
								/>
							</div>
							<div class="flex" v-if="shouldShowPagination && categoryName === 'live'">
								<Pagination class="mx-auto my-6" :current-page="currentPage" :pages="pages" @go-to-page="goToPage" />
							</div>
						</CollapseContainer>
					</div>
				</div>

				<ListingSearchEmpty v-if="noSearchMatches" :query="searchWidgetInput" noun="streams" @clear="clearSearch" />

				<EmptyState v-if="shouldShowPlaceholder" title="Create your first livestream"
					subtitle="Go live in minutes and reach your audience on any platform.">
					<template #illustration>
						<img width="120" src="@/assets/images/no-stream-02.svg" alt="No Stream">
					</template>
					<template #actions>
						<div class="flex flex-col w-[220px] mx-auto">
							<FormButton type="secondary" size="md" @click="$showModal('modal-demo-video')"
								class="text-center justify-center mb-2">
								<icon-base class="mr-2" fill="none">
									<icon-camera />
								</icon-base>
								Watch demo
							</FormButton>
							<Tooltip :active="atAllCreateLimits" wrap-trigger :html="true" :hide="1000" :content="livestreamCreateDisabledTitle">
								<FormButton size="md" @click="showCreateStreamSidebar" class="text-center justify-center w-full"
									:disabled="isLivestreamCreateDisabled">
									<icon-base class="mr-2" fill="none">
										<icon-camera-plus />
									</icon-base>
									New Livestream
								</FormButton>
							</Tooltip>
						</div>
					</template>
				</EmptyState>

				<!-- <add-stream-modal :typeFromBilling="this.action" @new-channel="onNewStream" /> -->
				<demo-video-modal videoid="nlAJJ_mROavGyHPRs8ZZw" />
				<confirm-modal message="Please update your subscription to get more concurrent streams."
					modal-id="increase-limit" modal-type="danger" ok-text="Yes" cancel-text="Cancel"
					@modal-confirm="gotoUpgrade" />
				<confirm-modal message="Would you like to delete this stream and all of its content?" modal-id="delete-stream"
					modal-type="danger" @modal-confirm="onStreamDeleteConfirm" />
				<confirm-modal message="Would you like to delete this stream and all of its content?"
					modal-id="delete-stream-showcased"
					body="This stream is included in the showcase. Removing it will remove the stream from the showcase as well."
					modal-type="danger" ok-text="Delete" cancel-text="Cancel" @modal-confirm="onStreamDeleteConfirmShowcased" />
				<confirm-modal
					message="Attention: Multistream has been discontinued. Set up a new Live Stream today for uninterrupted streaming with enhanced features, at no extra cost."
					modal-id="create-aio" ok-text="Create Live Stream" cancel-text="Cancel" @modal-confirm="openCreatestream" />
				<div v-if="deleteProcessing" class="vue-notification-group" style="width: 300px; top: 10px; right: 10px;">
					<div data-id="2" class="vue-notification-wrapper" style="transition: all 300ms ease 0s;">
						<div class="vue-notification-template vue-notification info">
							<div class="notification-content">Deleting... Don't refresh the page</div>
						</div>
					</div>
				</div>
				<SidebarAddStream ref="sidebarAddStream" />
			</section>
		</div>
		<div class="no-access w-full mt-0 pl-3" v-else>
			<div class="text-center pt-5 mt-5">
				<h4 class="text-dark-9 text-s-xl text-500 mb-1">You don't have permission to access this.</h4>
				<p class="text-dark-6 text-s-m">Contact the team owner at <a class="text-dark-6 text-s-m text-underline"
						:href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</p>
			</div>
		</div>
		<!-- Outside the branches above on purpose. An invitee has no grant yet,
		     so the page behind this can be loading, empty, or the no-permission
		     notice — and accepting is the only way out of all three. Mounted
		     with the view, it is listening whenever the prompt fires. -->
		<confirm-modal :message="'You are invited to the &quot;' + teamownerName + '&quot; team'"
			body="Join your team and start streaming incredible content together." modal-id="join-team" modal-size="md"
			ok-text="Join Team" cancel-text="Cancel" @modal-confirm="joinTeam" />
	</div>
</template>



